import Alpine from "alpinejs";
import type { WeightEntry, WeeklyRow } from "./types";
import { todayISO } from "./dates";
import { formatWeight, weightWhole, weightFrac } from "./format";
import { computeWeeklyRows } from "./aggregate";
import { createChartRenderer } from "./chart";
import { requestEntries, type EntriesResult } from "./api";
import { parseEntryInput } from "./entryInput";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;
const KEY_STORAGE_NAME = "weight-tracker-api-key";

// Monday..Sunday, in day-of-week order — also the order notes are colored in.
const DAY_ABBREVS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const NOTE_COLOR_VARS = ["--note-mon", "--note-tue", "--note-wed", "--note-thu", "--note-fri", "--note-sat", "--note-sun"];

// Alpine injects these "magic" properties/methods at runtime; they aren't
// part of the plain object we return from weightTracker(), so we declare
// them here to call them with type safety instead of casting to `any`.
interface AlpineMagics {
	$nextTick(callback: () => void): Promise<void>;
}

function weightTracker() {
	const chartRenderer = createChartRenderer();

	return {
		hasKey: false,
		keyInput: "",
		apiKey: "",
		error: "",
		loading: false,
		saving: false,
		entries: [] as WeightEntry[],
		form: {
			date: todayISO(),
			weight: "",
			note: "",
		},
		// editDate is the date being edited (always set while the modal is
		// open); editEntry is the entry that already existed for that date, or
		// null when the modal was opened from an empty day (create instead of
		// edit/PUT).
		editDate: null as string | null,
		editEntry: null as WeightEntry | null,
		editForm: {
			date: "",
			weight: "",
			note: "",
		},
		editError: "",
		editSaving: false,
		importError: "",
		importing: false,

		get todayLabel(): string {
			return new Date().toLocaleDateString(undefined, {
				weekday: "long",
				month: "long",
				day: "numeric",
			});
		},

		// Ascending (oldest week first) — used as-is for the chart's x-axis.
		get weeklyRows(): WeeklyRow[] {
			return computeWeeklyRows(this.entries);
		},

		// Newest week first — used for the table.
		get weeklyRowsDesc(): WeeklyRow[] {
			return [...this.weeklyRows].reverse();
		},

		fmt: formatWeight,
		whole: weightWhole,
		frac: weightFrac,
		dayAbbrev: (dayIndex: number) => DAY_ABBREVS[dayIndex],
		noteAccentStyle: (dayIndex: number) => `--note-accent: var(${NOTE_COLOR_VARS[dayIndex]})`,

		// A note-only entry has no weight, so the weight summary/chart/delta
		// below only ever look at entries that actually logged one.
		get weighedEntries(): Array<WeightEntry & { weight: number }> {
			return this.entries.filter((e): e is WeightEntry & { weight: number } => e.weight !== undefined);
		},

		get latestWeight(): string {
			const weighed = this.weighedEntries;
			if (weighed.length === 0) return "—";
			return formatWeight(weighed[weighed.length - 1].weight);
		},

		get deltaLabel(): string {
			const weighed = this.weighedEntries;
			if (weighed.length < 2) return "";
			const last = weighed[weighed.length - 1].weight;
			const prev = weighed[weighed.length - 2].weight;
			const diff = last - prev;
			const sign = diff > 0 ? "+" : "";
			return `${sign}${formatWeight(diff)} kg since last entry`;
		},

		get deltaClass(): string {
			const weighed = this.weighedEntries;
			if (weighed.length < 2) return "flat";
			const last = weighed[weighed.length - 1].weight;
			const prev = weighed[weighed.length - 2].weight;
			if (last > prev) return "up";
			if (last < prev) return "down";
			return "flat";
		},

		init() {
			const stored = localStorage.getItem(KEY_STORAGE_NAME);
			if (stored) {
				this.apiKey = stored;
				this.hasKey = true;
				this.fetchEntries();
			}
		},

		unlock() {
			if (!this.keyInput.trim()) return;
			this.apiKey = this.keyInput.trim();
			localStorage.setItem(KEY_STORAGE_NAME, this.apiKey);
			this.hasKey = true;
			this.error = "";
			this.fetchEntries();
		},

		authHeaders(): Record<string, string> {
			return {
				Authorization: `Bearer ${this.apiKey}`,
				"Content-Type": "application/json",
			};
		},

		// Applies the outcome of a requestEntries() call to shared state: on
		// success, updates entries and re-renders the chart; on 401, locks the
		// app back down; otherwise writes the message into the given error
		// field. Returns whether it succeeded, so callers can chain their own
		// side effect (clearing a field, closing the modal) onto success only.
		applyEntriesResult(result: EntriesResult, errorField: "error" | "editError" | "importError" = "error"): boolean {
			if (result.ok) {
				this.entries = result.entries;
				(this as unknown as AlpineMagics).$nextTick(() => chartRenderer.render(this.entries, this.weeklyRows));
				return true;
			}
			if (result.unauthorized) {
				localStorage.removeItem(KEY_STORAGE_NAME);
				this.hasKey = false;
				this.apiKey = "";
				this.error = "That key was rejected. Try again.";
				return false;
			}
			this[errorField] = result.error;
			return false;
		},

		async fetchEntries() {
			this.loading = true;
			this.error = "";
			const result = await requestEntries(`${API_BASE_URL}/api/entries`, { headers: this.authHeaders() }, {
				failure: "Could not load entries.",
			});
			this.applyEntriesResult(result);
			this.loading = false;
		},

		async logWeight() {
			const parsed = parseEntryInput(this.form.date, this.form.weight, this.form.note);
			if (!parsed.ok) {
				this.error = parsed.error;
				return;
			}
			this.saving = true;
			this.error = "";
			const result = await requestEntries(
				`${API_BASE_URL}/api/entries`,
				{
					method: "POST",
					headers: this.authHeaders(),
					body: JSON.stringify({ date: parsed.date, weight: parsed.weight, note: parsed.note }),
				},
				{
					conflict: "There's already an entry for that date — click it in the table below to edit it.",
					failure: "Could not save entry.",
				},
			);
			if (this.applyEntriesResult(result)) {
				this.form.weight = "";
				this.form.note = "";
			}
			this.saving = false;
		},

		// Opens the day editor for `date`, whether or not it already has an
		// entry — `entry` is null for an empty day, which saveEdit() then
		// creates (POST) instead of edits (PUT).
		openEdit(date: string, entry: WeightEntry | null) {
			this.editDate = date;
			this.editEntry = entry;
			this.editForm = {
				date,
				weight: entry?.weight !== undefined ? entry.weight.toString() : "",
				note: entry?.note ?? "",
			};
			this.editError = "";
		},

		closeEdit() {
			this.editDate = null;
			this.editEntry = null;
		},

		async saveEdit() {
			if (!this.editDate) return;
			const parsed = parseEntryInput(this.editForm.date, this.editForm.weight, this.editForm.note);
			if (!parsed.ok) {
				this.editError = parsed.error;
				return;
			}
			this.editSaving = true;
			this.editError = "";
			const body = JSON.stringify({ date: parsed.date, weight: parsed.weight, note: parsed.note });
			const messages = { conflict: "There's already an entry for that date.", failure: "Could not save changes." };
			const result = this.editEntry
				? await requestEntries(
						`${API_BASE_URL}/api/entries/${encodeURIComponent(this.editEntry.date)}`,
						{ method: "PUT", headers: this.authHeaders(), body },
						messages,
					)
				: await requestEntries(`${API_BASE_URL}/api/entries`, { method: "POST", headers: this.authHeaders(), body }, messages);
			if (this.applyEntriesResult(result, "editError")) this.closeEdit();
			this.editSaving = false;
		},

		async deleteEditEntry() {
			if (!this.editEntry) return;
			await this.deleteEntry(this.editEntry.date);
			this.closeEdit();
		},

		async deleteEntry(date: string) {
			const result = await requestEntries(
				`${API_BASE_URL}/api/entries/${encodeURIComponent(date)}`,
				{ method: "DELETE", headers: this.authHeaders() },
				{ failure: "Could not delete entry." },
			);
			this.applyEntriesResult(result);
		},

		// Downloads the currently loaded entries as a JSON file, for the user to
		// keep somewhere outside this app in case a bad click or a bug here
		// ever wipes the KV store.
		exportBackup() {
			const blob = new Blob([JSON.stringify({ entries: this.entries }, null, 2)], { type: "application/json" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `weight-log-backup-${todayISO()}.json`;
			a.click();
			URL.revokeObjectURL(url);
		},

		async importBackup(event: Event) {
			const input = event.target as HTMLInputElement;
			const file = input.files?.[0];
			if (!file) return;
			this.importing = true;
			this.importError = "";
			try {
				const parsed = JSON.parse(await file.text());
				const entries = Array.isArray(parsed) ? parsed : parsed?.entries;
				if (!Array.isArray(entries)) throw new Error("That file doesn't look like a backup export.");
				const result = await requestEntries(
					`${API_BASE_URL}/api/entries/import`,
					{ method: "POST", headers: this.authHeaders(), body: JSON.stringify({ entries }) },
					{ failure: "Could not restore that backup." },
				);
				this.applyEntriesResult(result, "importError");
			} catch (e) {
				this.importError = e instanceof Error ? e.message : "Something went wrong.";
			} finally {
				this.importing = false;
				input.value = "";
			}
		},
	};
}

// Expose to Alpine's x-data
(window as unknown as { weightTracker: typeof weightTracker }).weightTracker = weightTracker;

Alpine.start();
